import { useState } from "react";

import { Alert } from "@/components/alert/alert";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { useDuelQueue } from "@/hooks/use-duel-queue";
import { useAuth } from "@/providers/auth-provider";
import { useDuel } from "@/providers/duel-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";

import { PlayMenuCampaignTeaser } from "./play-menu-campaign-teaser";
import { PlayMenuCommandList } from "./play-menu-command-list";
import { PlayMenuStatusWindow } from "./play-menu-status-window";

export function PlayMenuView() {
  const { setCurrentRoute } = useSPAContext();
  const { getAccessToken, state: authState } = useAuth();
  const { connectToDuel } = useDuel();

  const { queueStatus, currentDuelId } = useDuelQueue(true);
  const [isInitializingDuel, setIsInitializingDuel] = useState(false);

  const handleStartDuel = async () => {
    if (isInitializingDuel) return;
    const accessToken = getAccessToken();
    if (!accessToken || authState.status !== "authenticated") {
      Alert("Authentication error. Please try again");
      return;
    }
    if (!authState.user?.player_id) {
      Alert("Authentication error. Please try again");
      return;
    }
    setCurrentRoute(RouteNames.ARENA_TEAM_SELECT);
  };

  const handleReconnectToDuel = async () => {
    if (isInitializingDuel || !currentDuelId) return;
    const accessToken = getAccessToken();
    if (!accessToken || authState.status !== "authenticated") {
      Alert("Authentication error. Please try again");
      return;
    }
    if (!authState.user?.player_id) {
      Alert("Authentication error. Please try again");
      return;
    }
    setIsInitializingDuel(true);
    try {
      setCurrentRoute(RouteNames.DUEL_QUEUE);
      const wsUrl = import.meta.env.VITE_WS_API_URL;
      if (!wsUrl) {
        Alert("Internal server error. Please contact support");
        return;
      }
      await connectToDuel(
        currentDuelId,
        authState.user.player_id,
        accessToken,
        wsUrl,
        true,
      );
    } catch (error) {
      console.error("Failed to reconnect to duel:", error);
      Alert("Failed to reconnect to duel");
    } finally {
      setIsInitializingDuel(false);
    }
  };

  const isDuelActive = queueStatus === "IN_DUEL" || queueStatus === "MATCHED";
  const isDuelWaiting = queueStatus === "WAITING";

  return (
    <div className="relative flex flex-col bg-pk-night-1/95 backdrop-blur-md w-full h-full overflow-y-auto pb-28">
      <div className="px-4 pt-5 pb-3">
        <StrokedText strokeWidth={12} fontSize={32} letterSpacing={2}>
          PLAY
        </StrokedText>
      </div>

      <div className="flex flex-col gap-3 px-3">
        <PlayMenuStatusWindow />
        <PlayMenuCampaignTeaser />
        <PlayMenuCommandList
          isDuelActive={isDuelActive}
          isDuelWaiting={isDuelWaiting}
          isInitializingDuel={isInitializingDuel}
          onArena={handleStartDuel}
          onReconnect={handleReconnectToDuel}
        />
      </div>
    </div>
  );
}
